import React, { useCallback, useEffect, useRef, useState } from 'react';
import { buildApiUrl } from '../utils/apiConfig';
import { redirectToLogin } from '../utils/auth';
import { athleteProfileFetchPath } from '../utils/viewedAthlete';
import { fetchMediaKit, MediaKitData } from '../services/mediaKitService';
import MediaKitWizard from './MediaKitWizard';
import MediaKitView from './MediaKitView';
import '../styles/media-kit.css';

interface MediaKitTabProps {
    athleteProfileId?: string;
    onCompletedKitChange?: (state: { completed: boolean; athleteUserId?: number }) => void;
    readOnly?: boolean;
}

export default function MediaKitTab({ athleteProfileId, onCompletedKitChange, readOnly = false }: MediaKitTabProps) {
    const [data, setData] = useState<MediaKitData | null>(null);
    const [athleteUserId, setAthleteUserId] = useState<number | undefined>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const wizardInProgressRef = useRef(false);
    wizardInProgressRef.current = data != null && !data.completed;

    const resolveAthleteUserId = useCallback(async (): Promise<number | undefined> => {
        const profilePath = athleteProfileFetchPath(athleteProfileId);
        if (!profilePath) {
            return undefined;
        }
        const res = await fetch(buildApiUrl(profilePath), {
            credentials: 'include',
            headers: { Accept: 'application/json' },
        });
        if (res.status === 401 || res.status === 403) {
            redirectToLogin(true);
            return undefined;
        }
        if (res.ok) {
            const profile = await res.json();
            return profile.userId ?? profile.id;
        }
        return undefined;
    }, [athleteProfileId]);

    const loadMediaKit = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const userId = await resolveAthleteUserId();
            setAthleteUserId(userId);
            const kit = await fetchMediaKit(userId);
            setData(kit);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load media kit');
        } finally {
            setLoading(false);
        }
    }, [resolveAthleteUserId]);

    useEffect(() => {
        loadMediaKit();
    }, [loadMediaKit]);

    useEffect(() => {
        onCompletedKitChange?.({
            completed: Boolean(data?.completed),
            athleteUserId: data?.athleteUserId,
        });
    }, [data, onCompletedKitChange]);

    useEffect(() => {
        const refreshOnVisible = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            // Reloading sets loading=true and unmounts MediaKitWizard, resetting wizard step.
            if (wizardInProgressRef.current) {
                return;
            }
            loadMediaKit();
        };
        document.addEventListener('visibilitychange', refreshOnVisible);
        return () => document.removeEventListener('visibilitychange', refreshOnVisible);
    }, [loadMediaKit]);

    if (loading) {
        return <div className="media-kit-container media-kit-tab-root"><div className="loading-spinner">Loading media kit...</div></div>;
    }

    if (error) {
        return <div className="media-kit-container media-kit-tab-root"><div className="error-message">{error}</div></div>;
    }

    if (data && (data.completed || readOnly)) {
        return (
            <div className="media-kit-container media-kit-tab-root">
                <MediaKitView
                    data={data}
                    athleteUserId={data.athleteUserId}
                    onUpdated={setData}
                    readOnly={readOnly}
                />
            </div>
        );
    }

    return (
        <div className="media-kit-container media-kit-tab-root">
            <MediaKitWizard
                athleteUserId={athleteUserId}
                initialData={data}
                onComplete={(completed) => setData(completed)}
            />
        </div>
    );
}
